import type {
  SubmissionValidationResultsResponse,
  ValidationResultEntry,
} from '@/api/v2Client';

/**
 * phase-validation-surfaces (Sharon, 2026-07-23) — ONE definition of what the
 * compiler's validation surfaces count, list, and say.
 *
 * ## The finding this closes
 *
 * The Compiler showed the same validation run through several chrome:
 *   (a) "4 check issues"        top-bar chip      — errors only
 *   (b) "4 errors, 2 warnings"  toolbar badge     — errors AND warnings
 *   (c) "4 validation errors"   bottom drawer     — errors only
 * The numbers agreed (verified against both the `/validation-results` and the
 * polled `/validation-status` endpoints: 4 errors, 2 warnings on Eupravia Seq
 * 0001) — but (a) and (c) DROPPED the warnings at the display layer. A warning
 * a user never sees is a warning that reaches the agency. This module makes the
 * counts and their phrasing come from one place, so every surface says the same
 * thing.
 *
 * ## The failing-entry predicate
 *
 * A result "fails" when its status is `fail` AND its severity is error or
 * warning. A `skip` (rule couldn't evaluate) or `pass` is not a failure. This
 * mirrors the server's own summary — the counts are computed there over the
 * latest-per-(rule,target) set — so the entry LIST the UI derives here lines up
 * with the COUNTS the server sends.
 */

export type FailSeverity = 'error' | 'warning';

/** A failing result, narrowed to the severity the UI renders. */
export interface FailingEntry extends ValidationResultEntry {
  readonly severity: FailSeverity;
}

export function isFailing(entry: ValidationResultEntry): entry is FailingEntry {
  return (
    entry.status === 'fail' && (entry.severity === 'error' || entry.severity === 'warning')
  );
}

/**
 * Every failing entry (errors and warnings) for the run, errors first so the
 * worklist reads worst-first. Order within a severity is preserved.
 */
export function failingEntries(
  data: SubmissionValidationResultsResponse | null | undefined,
): FailingEntry[] {
  const failing = (data?.results ?? []).filter(isFailing);
  return failing.sort((a, b) => {
    if (a.severity === b.severity) return 0;
    return a.severity === 'error' ? -1 : 1;
  });
}

export interface ValidationCounts {
  readonly errorCount: number;
  readonly warningCount: number;
}

/** Zero when there is no run yet. */
export function countsFrom(
  data: SubmissionValidationResultsResponse | null | undefined,
): ValidationCounts {
  return {
    errorCount: data?.errorCount ?? 0,
    warningCount: data?.warningCount ?? 0,
  };
}

function plural(n: number, word: string): string {
  return `${n} ${word}${n === 1 ? '' : 's'}`;
}

/**
 * The ONE phrasing of a validation summary, used by every surface so they can
 * never disagree again. Warnings are ALWAYS included when present — never
 * silently dropped.
 *
 *   { error: 4, warning: 2 } → "4 errors, 2 warnings"
 *   { error: 4, warning: 0 } → "4 errors"
 *   { error: 0, warning: 2 } → "2 warnings"
 *   { error: 0, warning: 0 } → "No issues"
 */
export function formatValidationCounts(counts: ValidationCounts): string {
  const { errorCount, warningCount } = counts;
  const parts: string[] = [];
  if (errorCount > 0) parts.push(plural(errorCount, 'error'));
  if (warningCount > 0) parts.push(plural(warningCount, 'warning'));
  return parts.length > 0 ? parts.join(', ') : 'No issues';
}

/** True when there is at least one error or warning to show. */
export function hasIssues(counts: ValidationCounts): boolean {
  return counts.errorCount > 0 || counts.warningCount > 0;
}

/** Full reports include every recorded outcome; worklists use failingEntries. */
export function summarizeValidationReport(results: ReadonlyArray<ValidationResultEntry>) {
  const counts = { pass: 0, fail: 0, skip: 0 };
  for (const result of results) counts[result.status]++;
  const statusRank = { fail: 0, pass: 1, skip: 2 };
  const severityRank = { error: 0, warning: 1, info: 2 };
  const ordered = [...results].sort((a, b) =>
    statusRank[a.status] - statusRank[b.status] ||
    severityRank[a.severity] - severityRank[b.severity] ||
    a.ruleName.localeCompare(b.ruleName) ||
    a.targetType.localeCompare(b.targetType) ||
    a.targetId.localeCompare(b.targetId),
  );
  return { counts, ordered };
}
